import { RouteNames } from "_route-names/route-names";
import {
  LISTING_COMPLIANCES_REQUEST,
  LISTING_COMPLIANCES_SUCCESS,
  LISTING_COMPLIANCES_FAILURE,
  ADD_COMPLIANCE_REQUEST,
  ADD_COMPLIANCE_SUCCESS,
  ADD_COMPLIANCE_FAILURE,
  GET_COMPLIANCE_BY_ID_REQUEST,
  GET_COMPLIANCE_BY_ID_SUCCESS,
  GET_COMPLIANCE_BY_ID_FAILURE,
  DELETE_COMPLIANCE_REQUEST,
  DELETE_COMPLIANCE_SUCCESS,
  DELETE_COMPLIANCE_FAILURE,
  UPDATE_COMPLIANCE_REQUEST,
  UPDATE_COMPLIANCE_FAILURE,
  SET_SINGLE_COMPLIANCE,
  GET_ALL_COMPLIANCES_REQUEST,
  GET_ALL_COMPLIANCES_FAILURE,
  GET_ALL_COMPLIANCES_SUCCESS,
  SET_SINGLE_COMPLIANCE_ALL,
  SET_COMPLIANCE_PROPERTY_BY_ID,
  UPDATE_COMPLIANCE_SUCCESS,
  GET_COMPLIANCE_HISTORY_REQUEST,
  GET_COMPLIANCE_HISTORY_SUCCESS,
  GET_COMPLIANCE_HISTORY_FAILURE,
  COMPLIANCE_TYPES_REQUEST,
  COMPLIANCE_TYPES_SUCCESS,
  COMPLIANCE_TYPES_FAILURE,
  COMPLIANCE_SUMMARY_REQUEST,
  COMPLIANCE_SUMMARY_SUCCESS,
  COMPLIANCE_SUMMARY_FAILURE
} from "./types";
import { DEFAULT_COMPLIANCE_TYPES } from "_constants/complianceTypes";

const initialState = {
  compliances: [],
  pagination: { total: 0, page: 0, limit: 25, totalPages: 1 },
  loading: false,
  singleProperty: null,
  singleCompliance: null,
  allCompliances: [],
  allCompliancesPagination: { total: 0, page: 0, limit: 25, totalPages: 1 },
  // Status-bucket counts for the current search/tag scope (ignores statusFilter
  // itself) — drives the filter pills so their counts track the search box.
  // Starts null so the pills fall back to complianceSummary until the first
  // list fetch resolves.
  allCompliancesStatusCounts: null,
  // Summary tiles on My Compliance. Its own loading flag — see getComplianceSummary.
  complianceSummary: { overdue: 0, dueIn60Days: 0, current: 0, inactive: 0, total: 0, all: 0 },
  summaryLoading: false,
  redirectedFrom: "property",
  history: [],
  historyLoading: false,
  // Seeded with the built-in list so the dropdown (and the "is this type custom?"
  // check behind the record-level rename) works before the fetch resolves.
  complianceTypes: { defaults: DEFAULT_COMPLIANCE_TYPES, custom: [], loading: false }
};

const compliancesReducer = (state = initialState, action) => {
  switch (action.type) {
    case LISTING_COMPLIANCES_REQUEST:
    case GET_ALL_COMPLIANCES_REQUEST:
    case ADD_COMPLIANCE_REQUEST:
    case DELETE_COMPLIANCE_REQUEST:
    case GET_COMPLIANCE_BY_ID_REQUEST:
    case UPDATE_COMPLIANCE_REQUEST:
      return {
        ...state,
        loading: true
      };
    case GET_COMPLIANCE_HISTORY_REQUEST:
      return {
        ...state,
        historyLoading: true
      };
    case GET_COMPLIANCE_HISTORY_SUCCESS:
      return {
        ...state,
        history: Array.isArray(action.payload) ? action.payload : [],
        historyLoading: false
      };
    case GET_COMPLIANCE_HISTORY_FAILURE:
      return {
        ...state,
        historyLoading: false,
        history: []
      };
    case COMPLIANCE_TYPES_REQUEST:
      return {
        ...state,
        complianceTypes: { ...state.complianceTypes, loading: true }
      };
    case COMPLIANCE_TYPES_SUCCESS:
      return {
        ...state,
        complianceTypes: {
          defaults: Array.isArray(action.payload?.defaults) ? action.payload.defaults : DEFAULT_COMPLIANCE_TYPES,
          custom: Array.isArray(action.payload?.custom) ? action.payload.custom : [],
          loading: false
        }
      };
    // Keep whatever the store already holds (at minimum the built-in defaults)
    // so a failed fetch degrades to the standard list rather than an empty one.
    case COMPLIANCE_TYPES_FAILURE:
      return {
        ...state,
        complianceTypes: { ...state.complianceTypes, loading: false }
      };
    case LISTING_COMPLIANCES_SUCCESS: {
      const listings = action.payload.listings ?? action.payload;
      const pagination = action.payload.pagination ?? {
        total: Array.isArray(listings) ? listings.length : 0,
        page: action.payload.page ?? 0,
        limit: action.payload.limit ?? 25,
        totalPages: 1
      };
      return {
        ...state,
        compliances: Array.isArray(listings) ? listings : [],
        pagination,
        loading: false
      };
    }
    case SET_COMPLIANCE_PROPERTY_BY_ID:
      return {
        ...state,
        singleProperty: action.payload,
        loading: false
      };

    case LISTING_COMPLIANCES_FAILURE:
    case ADD_COMPLIANCE_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    case ADD_COMPLIANCE_SUCCESS: {
      const compliances = [...state.compliances];
      const index = compliances.findIndex((e) => e._id === action.payload.propertyId);
      if (index !== -1) {
        compliances[index].compliances = compliances[index].compliances || [];
        compliances[index].compliances.push(action.payload);
        const status = determineStatus(action.payload.validTo);
        if (compliances[index].counts && status) compliances[index].counts[status] = (compliances[index].counts[status] || 0) + 1;
      }
      return (() => {
        const property = state.singleProperty ? { ...state.singleProperty } : null;
        if (!property || !Array.isArray(property.compliances)) {
          return { ...state, loading: false, success: true, compliances };
        }
        const nextCompliances = [...property.compliances];
        const idx = nextCompliances.findIndex((e) => e._id === action.payload._id);
        if (idx === -1) nextCompliances.push(action.payload);
        property.compliances = nextCompliances;
        property.counts = recalculateCountsFromCompliances(nextCompliances);

        const propertyId = action.payload.propertyId?._id || action.payload.propertyId;
        const listWithUpdatedCounts = propertyId
          ? compliances.map((listing) =>
              String(listing._id) === String(propertyId)
                ? { ...listing, counts: property.counts }
                : listing
            )
          : compliances;

        return {
          ...state,
          loading: false,
          success: true,
          compliances: listWithUpdatedCounts,
          singleProperty: property
        };
      })();
    }
    case GET_COMPLIANCE_BY_ID_SUCCESS:
      return {
        ...state,
        loading: false,
        singleCompliance: action.payload
      };
    case DELETE_COMPLIANCE_SUCCESS: {
      const propertyId = action.payload.propertyId;
      const nextState = (() => {
        const property = state.singleProperty ? { ...state.singleProperty } : null;
        if (!property || !Array.isArray(property.compliances)) {
          return { ...state, loading: false, success: true };
        }
        const index = property.compliances.findIndex((e) => e._id === action.payload.id);
        if (index >= 0) {
          const status = determineStatus(property.compliances[index].validTo);
          if (status && property.counts && property.counts[status] != null) {
            property.counts = { ...property.counts, [status]: Math.max(0, property.counts[status] - 1) };
          }
          property.compliances = property.compliances.filter((_, i) => i !== index);
        }
        // Sidebar highlight. Points at My Compliance rather than the old
        // /Compliance/:id property list, which no longer exists — per-property
        // compliance now lives on the property page's Compliance tab.
        localStorage.setItem("current-menue", JSON.stringify(RouteNames.complaince));

        const compliances = state.compliances && propertyId
          ? state.compliances.map((listing) =>
              String(listing._id) === String(propertyId)
                ? { ...listing, counts: property.counts }
                : listing
            )
          : state.compliances;

        return {
          ...state,
          loading: false,
          success: true,
          singleProperty: property,
          compliances: compliances || state.compliances
        };
      })();
      return nextState;
    }
    case SET_SINGLE_COMPLIANCE:
      return {
        ...state,
        singleCompliance: { ...state.singleProperty.compliances.find((e) => e._id === action.payload), property: state.singleProperty },
        redirectedFrom: "property",
        loading: false
      };

    // Seeds the details page from the row the user clicked so it can paint before
    // its own fetch lands. The row is normally on the current page, but a stale
    // click (list refreshed underneath) must not throw — the details page fetches
    // by id regardless.
    case SET_SINGLE_COMPLIANCE_ALL: {
      const row = state.allCompliances.find((e) => e._id === action.payload);
      if (!row) return { ...state, redirectedFrom: "compliances", loading: false };
      return {
        ...state,
        singleCompliance: { ...row, property: row.propertyId },
        redirectedFrom: "compliances",
        loading: false
      };
    }

    case GET_ALL_COMPLIANCES_SUCCESS: {
      const list = action.payload.compliances ?? action.payload;
      const pagination = action.payload.pagination ?? {
        total: Array.isArray(list) ? list.length : 0,
        page: 0,
        limit: 25,
        totalPages: 1,
      };
      return {
        ...state,
        allCompliances: Array.isArray(list) ? list : [],
        allCompliancesPagination: pagination,
        allCompliancesStatusCounts: action.payload.statusCounts ?? state.allCompliancesStatusCounts,
        loading: false,
      };
    }

    // Record-field update. The details page re-fetches via getComplianceById to
    // refresh its own view (record + documents), so we only reconcile the
    // property list/counts here and leave singleCompliance untouched.
    case UPDATE_COMPLIANCE_SUCCESS:
      return (() => {
        const property = state.singleProperty ? { ...state.singleProperty } : null;
        if (!property || !Array.isArray(property.compliances)) {
          return { ...state, loading: false };
        }
        const index = property.compliances.findIndex((e) => e._id === action.payload._id);
        const nextCompliances = [...property.compliances];
        if (index >= 0) nextCompliances[index] = { ...nextCompliances[index], ...action.payload };
        property.compliances = nextCompliances;
        property.counts = recalculateCountsFromCompliances(nextCompliances);

        const propertyId = action.payload.propertyId?._id || action.payload.propertyId;
        const compliances = state.compliances && propertyId
          ? state.compliances.map((listing) =>
              String(listing._id) === String(propertyId)
                ? { ...listing, counts: property.counts }
                : listing
            )
          : state.compliances;

        return {
          ...state,
          loading: false,
          singleProperty: property,
          compliances: compliances || state.compliances
        };
      })();
    case COMPLIANCE_SUMMARY_REQUEST:
      return {
        ...state,
        summaryLoading: true
      };
    case COMPLIANCE_SUMMARY_SUCCESS:
      return {
        ...state,
        summaryLoading: false,
        complianceSummary: {
          overdue: action.payload?.overdue ?? 0,
          dueIn60Days: action.payload?.dueIn60Days ?? 0,
          current: action.payload?.current ?? 0,
          inactive: action.payload?.inactive ?? 0,
          total: action.payload?.total ?? 0,
          all: action.payload?.all ?? 0
        }
      };
    case COMPLIANCE_SUMMARY_FAILURE:
      return {
        ...state,
        summaryLoading: false
      };
    case DELETE_COMPLIANCE_FAILURE:
    case UPDATE_COMPLIANCE_FAILURE:
    case GET_ALL_COMPLIANCES_FAILURE:
    case GET_COMPLIANCE_BY_ID_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    default:
      return state;
  }
};

export default compliancesReducer;

const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;

// Mirrors the server's bucketing: a record with no valid-to date is inactive and
// belongs to no bucket, so it is counted nowhere on the dashboard.
function determineStatus(validTo) {
  if (validTo == null) return null;
  const currentDate = new Date();
  const dueDate = new Date(validTo);
  if (dueDate < currentDate) return "overdue";
  const sixtyDaysFromNow = new Date(currentDate.getTime() + SIXTY_DAYS_MS);
  if (dueDate <= sixtyDaysFromNow) return "dueIn60Days";
  return "current";
}

function recalculateCountsFromCompliances(compliances) {
  const counts = { overdue: 0, dueIn60Days: 0, current: 0 };
  (compliances || []).forEach((c) => {
    const status = determineStatus(c.validTo);
    if (status && counts[status] !== undefined) counts[status] += 1;
  });
  return counts;
}
